import { FormikErrors, FormikProps } from 'formik'
import {
  FinanceComplianceExtraFieldConfig,
  FinanceFile,
  FinanceFinancialDocumentComplianceExtraFieldsConfig,
  SimpleError,
  StructuredError,
} from 'generated/resources/graphql'
import { camelCase } from 'lodash'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import app from 'modules/core/store/models/app'

import { CustomComplianceExtraFieldErrors, SaleInvoiceFormFields } from '../SaleInvoiceForm/types'
import { countryComplianceExtraFieldsConfig } from './complianceExtraFields'

const COMPLIANCE_EXTRA_FIELDS_PREFIX = 'compliance_extra_fields.'

export const trackFormSaveAction = (
  invoiceId: string | undefined,
  currentValues: SaleInvoiceFormFields,
  initialValues: SaleInvoiceFormFields
): void => {
  if (!invoiceId) return

  const modifiedInputs: string[] = []

  if (currentValues.currency !== initialValues.currency) modifiedInputs.push('currency')
  if (currentValues.salesInvoiceSequence !== initialValues.salesInvoiceSequence)
    modifiedInputs.push('salesInvoiceSequence')
  if (currentValues.operationDate !== initialValues.operationDate)
    modifiedInputs.push('operationDate')
  if (currentValues.dueDate !== initialValues.dueDate) modifiedInputs.push('dueDate')
  if (currentValues.legalEntity !== initialValues.legalEntity) modifiedInputs.push('legalEntity')
  if (currentValues.contact !== initialValues.contact) modifiedInputs.push('contact')
  if (currentValues.lineItems !== initialValues.lineItems) modifiedInputs.push('lineItems')
  if (currentValues.discountPercentage !== initialValues.discountPercentage)
    modifiedInputs.push('discountPercentage')
  if (currentValues.memo !== initialValues.memo) modifiedInputs.push('memo')
  if (currentValues.footer !== initialValues.footer) modifiedInputs.push('footer')
  if (currentValues.retentionTaxRate !== initialValues.retentionTaxRate)
    modifiedInputs.push('retentionTaxRate')
  if (currentValues.project !== initialValues.project) modifiedInputs.push('project')
  if (currentValues.subproject !== initialValues.subproject) modifiedInputs.push('subproject')
  if (currentValues.action !== initialValues.action) modifiedInputs.push('action')

  // If the only modified input is the action, don't track the edit event
  if (modifiedInputs.length === 1 && modifiedInputs.includes('action')) {
    return
  }

  track(trackingEvents.accounting.saleInvoices.submitEdit, {
    modified_inputs: modifiedInputs,
  })
}

const mapValidationErrorsToFormErrors = (
  errors: Record<string, string>
): FormikErrors<SaleInvoiceFormFields> => {
  const mappedErrors: FormikErrors<SaleInvoiceFormFields> = {
    currency: errors.currency || undefined,
    documentNumber: errors.documentNumber || undefined,
    salesInvoiceSequence: errors.serialSequenceId || undefined,
    operationDate: errors.operationDate || undefined,
    issueDate: errors.issueDate || undefined,
    dueDate: errors.dueDate || undefined,
    legalEntity: errors.legalEntityId || undefined,
    contact: errors.contactId || undefined,
    project: errors.projectId || undefined,
    subproject: errors.subprojectId || undefined,
    discountPercentage: errors.discountPercentage || undefined,
    lineItems: errors.lineItems || undefined,
    memo: errors.memo || undefined,
    footer: errors.footer || undefined,
    showMemo: errors.showMemo || undefined,
    showFooter: errors.showFooter || undefined,
    retentionTaxRate: errors.retentionTaxRateId || undefined,
    originalFinancialDocumentId: errors.originalFinancialDocumentId || undefined,
    documentType: errors.documentType || undefined,
    action: errors.action || undefined,
  }

  if (errors.serialSequenceId) {
    mappedErrors.salesInvoiceSequence = errors.serialSequenceId
  }

  if (errors.legalEntityId) {
    mappedErrors.legalEntity = errors.legalEntityId
  }

  if (errors.contactId) {
    mappedErrors.contact = errors.contactId
  }

  return mappedErrors
}

const isFormError = (error: SimpleError | StructuredError): error is StructuredError => {
  return error.__typename === 'StructuredError'
}

const isErrorMessage = (error: SimpleError | StructuredError): error is SimpleError => {
  return error.__typename === 'SimpleError'
}

const buildCustomComplianceExtraFieldErrors = (
  errors: Record<string, string>,
  selectedComplianceExtraFieldConfig: FinanceFinancialDocumentComplianceExtraFieldsConfig
) => {
  const customComplianceExtraFieldErrors: CustomComplianceExtraFieldErrors = {
    document: {},
    lineItems: {},
  }

  Object.keys(errors).forEach((errorKey) => {
    const errorValue = errors[errorKey]
    const parts = errorKey.split('.')
    let lineItemId: string | undefined = undefined
    let fieldId: string | undefined = undefined

    if (parts.length === 2) {
      fieldId = parts[0]
      lineItemId = parts[1]
    } else {
      fieldId = parts[0]
    }
    if (fieldId === undefined || errorValue === undefined) return

    const fieldConfigFound = selectedComplianceExtraFieldConfig.fields.find(
      (fieldConfig: FinanceComplianceExtraFieldConfig) => fieldConfig.name === fieldId
    )
    if (fieldConfigFound === undefined) return

    // We are building a structure like this:
    // {lineItems: {1: {'verifactu-field': 'error message', 'verifactu-field-2': 'error message'}}}
    // {document: {'verifactu-field': 'error message'}}
    if (lineItemId) {
      customComplianceExtraFieldErrors.lineItems[lineItemId] = {
        ...customComplianceExtraFieldErrors.lineItems[lineItemId],
        [fieldId]: errorValue,
      }
    } else {
      customComplianceExtraFieldErrors.document[fieldId] = errorValue
    }
  })
  return customComplianceExtraFieldErrors
}

export const handleErrorValidateForIssuing = (
  errors: (StructuredError | SimpleError)[],
  complianceExtraFieldsConfigs: FinanceFinancialDocumentComplianceExtraFieldsConfig[],
  form: FormikProps<SaleInvoiceFormFields>
) => {
  const selectedComplianceExtraFieldsConfig = countryComplianceExtraFieldsConfig(
    complianceExtraFieldsConfigs,
    form.values.legalEntity?.country
  )

  const formErrors = errors.filter(isFormError)
  const simpleErrors = errors.filter(isErrorMessage)

  if (formErrors.length > 0) {
    form.setErrors(mapNonComplianceErrorsToFormErrors(formErrors))

    if (!!selectedComplianceExtraFieldsConfig) {
      form.setValues({
        ...form.values,
        customComplianceExtraFieldErrors: mapComplianceErrorsToCustomComplianceExtraFieldErrors(
          formErrors,
          selectedComplianceExtraFieldsConfig
        ),
      })
    }
  } else {
    if (simpleErrors.length > 0) {
      app.setFlash({
        type: 'wrong',
        message: simpleErrors.map(({ message }) => message).join(', '),
        time: 3000,
      })
    }
  }
}

const mapNonComplianceErrorsToFormErrors = (formErrors: StructuredError[]) => {
  const otherErrors = formErrors.filter(
    (error) => !error.field.includes(COMPLIANCE_EXTRA_FIELDS_PREFIX)
  )
  const otherErrorsHash = otherErrors.reduce<Record<string, string>>((acc, { field, messages }) => {
    acc[camelCase(field)] = messages.join(', ')
    return acc
  }, {})
  return mapValidationErrorsToFormErrors(otherErrorsHash)
}

const mapComplianceErrorsToCustomComplianceExtraFieldErrors = (
  formErrors: StructuredError[],
  selectedComplianceExtraFieldsConfig: FinanceFinancialDocumentComplianceExtraFieldsConfig
) => {
  const verifactuErrors = formErrors.filter((error) =>
    error.field.includes(COMPLIANCE_EXTRA_FIELDS_PREFIX)
  )
  const verifactuErrorsHash = verifactuErrors.reduce<Record<string, string>>(
    (acc, { field, messages }) => {
      acc[field.replace(COMPLIANCE_EXTRA_FIELDS_PREFIX, '')] = messages.join(', ')
      return acc
    },
    {}
  )
  const customComplianceExtraFieldErrors = buildCustomComplianceExtraFieldErrors(
    verifactuErrorsHash,
    selectedComplianceExtraFieldsConfig
  )
  return customComplianceExtraFieldErrors
}

export const fetchLogoFile = async (logoFileData: FinanceFile): Promise<File | undefined> => {
  if (!logoFileData?.url) return undefined

  try {
    const response = await fetch(logoFileData.url)
    const blob = await response.blob()
    return new File([blob], logoFileData.filename, { type: logoFileData.contentType })
  } catch (errorLogo) {
    return undefined
  }
}
